import PredictiveSearch from './PredictiveSearch';
import { debounce } from '../../helpers';
import { disablePageScroll, enablePageScroll } from '@fluejs/noscroll';

const selectors = {
  toggle: '[data-header-search-toggle]',
  surface: '[data-header-search]',
  form: '[data-header-search-form]',
  input: '[data-header-search-input]',
  clear: '[data-header-search-clear]',
  close: '[data-header-search-close]',
  results: '[data-header-search-results]',
  defaultPanel: '[data-search-default]',
  pill: '[data-header-pill]',
};

const SEARCH_OPEN_CLASS = 'header-pill--search-open';
const DEBOUNCE_MS = 300;

/**
 * The header's search surface — a full-screen sheet below `lg`, a panel docked under
 * the header pill at `lg`. One instance drives both: the same markup is restyled at
 * the breakpoint, so there is a single input, a single results mount and a single
 * request in flight.
 */
export default class HeaderSearch {
  /**
   * @param {HTMLElement} root - the section root to search within
   * @param {AbortSignal} signal - aborts every listener this component attaches
   */
  constructor(root, signal) {
    this.root = root;
    this.surface = root.querySelector(selectors.surface);
    if (!this.surface) return;

    this.pill = root.querySelector(selectors.pill);
    this.toggle = root.querySelector(selectors.toggle);
    this.form = this.surface.querySelector(selectors.form);
    this.input = this.surface.querySelector(selectors.input);
    this.clearButton = this.surface.querySelector(selectors.clear);
    this.closeButton = this.surface.querySelector(selectors.close);
    this.results = this.surface.querySelector(selectors.results);

    this.isOpen = false;
    this.scrollLocked = false;

    // The pre-typing panel is rendered server-side into the mount from the Global
    // Search theme settings. Caching it here means emptying the field can put it back
    // without a request, and an unconfigured one leaves the panel hidden as before.
    this.defaultMarkup = this.results?.innerHTML.trim() ?? '';
    this.hasDefault = Boolean(this.defaultMarkup);

    // `limit` is the API's TOTAL cap across every resource type, not a per-type one,
    // and 10 is its maximum. At 5, one suggestion plus two collections left room for
    // only two of seven matching products.
    this.predictiveSearch = new PredictiveSearch('predictive-search', {
      limit: 10,
      fields: 'body,product_type,tag,title,variants.sku,variants.title,vendor',
    });

    this.toggle?.addEventListener('click', this.open, { signal });
    this.closeButton?.addEventListener('click', this.close, { signal });
    this.clearButton?.addEventListener('click', this.clear, { signal });
    this.input?.addEventListener('input', this.onInput, { signal });
  }

  open = () => {
    if (!this.surface || this.isOpen) return;

    this.isOpen = true;
    this.surface.removeAttribute('hidden');
    this.pill?.classList.add(SEARCH_OPEN_CLASS);
    this.toggle?.setAttribute('aria-expanded', 'true');
    this.lockScroll();
    this.syncClearButton();
    this.showDefault();
    this.input?.focus();
  };

  close = () => {
    if (!this.surface || !this.isOpen) return;

    this.isOpen = false;
    this.surface.setAttribute('hidden', '');
    this.pill?.classList.remove(SEARCH_OPEN_CLASS);
    this.toggle?.setAttribute('aria-expanded', 'false');
    this.unlockScroll();
    if (this.input) this.input.value = '';
    this.syncClearButton();
    this.resetPanel();
  };

  clear = () => {
    if (!this.input) return;

    this.input.value = '';
    this.syncClearButton();
    this.showDefault();
    this.input.focus();
  };

  /** True when the click landed inside the search surface or on its toggle. */
  contains = target =>
    Boolean(this.surface?.contains(target) || this.toggle?.contains(target));

  // Reference-counted in @fluejs/noscroll, so the lock is tracked rather than
  // assumed — an unbalanced release would unlock the page under an open drawer.
  lockScroll = () => {
    if (this.scrollLocked) return;
    disablePageScroll();
    this.scrollLocked = true;
  };

  unlockScroll = () => {
    if (!this.scrollLocked) return;
    enablePageScroll();
    this.scrollLocked = false;
  };

  syncClearButton = () => {
    if (!this.clearButton) return;
    const hasValue = Boolean(this.input?.value.trim());
    this.clearButton.toggleAttribute('hidden', !hasValue);
  };

  onInput = debounce(() => {
    this.syncClearButton();

    const terms = this.input?.value.trim();
    if (!terms) {
      this.showDefault();
      return;
    }

    // Tag the request so a slow response for an earlier term cannot overwrite the
    // results for a later one.
    this.latestTerms = terms;

    this.predictiveSearch
      .query(terms)
      .then(response => {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then(text => {
        if (this.latestTerms !== terms || !this.isOpen) return;

        const markup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search');

        if (!markup) throw new Error('Missing predictive search section');

        this.showResults(markup.innerHTML);
      })
      .catch(error => {
        // Fall back to the default panel rather than an empty box.
        this.showDefault();
        throw error;
      });
  }, DEBOUNCE_MS);

  showResults = markup => {
    if (!this.results) return;

    this.results.innerHTML = markup;
    this.showPanel();
  };

  /** Puts the cached pre-typing panel back, or hides the panel if there is none. */
  showDefault = () => {
    if (!this.results) return;

    if (!this.hasDefault) {
      this.hidePanel();
      return;
    }

    if (!this.results.querySelector(selectors.defaultPanel)) {
      this.results.innerHTML = this.defaultMarkup;
    }
    this.showPanel();
  };

  /** Hides the panel and restores the default, so the next open starts from it. */
  resetPanel = () => {
    if (!this.results) return;

    this.results.innerHTML = this.hasDefault ? this.defaultMarkup : '';
    this.hidePanel();
  };

  showPanel = () => {
    this.results?.removeAttribute('hidden');
    this.input?.setAttribute('aria-expanded', 'true');
  };

  hidePanel = () => {
    this.results?.setAttribute('hidden', '');
    this.input?.setAttribute('aria-expanded', 'false');
  };

  destroy = () => {
    this.unlockScroll();
  };
}
